import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    agency_name: '', agent_name: '', email: '', password: '', agent_slug: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const toSlug = (val) =>
    val
      .normalize('NFD')                    // decompose accented chars (é → e + combining accent)
      .replace(/[\u0300-\u036f]/g, '')     // strip the combining accents
      .toLowerCase()
      .replace(/\s+/g, '-')               // spaces → hyphens
      .replace(/[^a-z0-9-]/g, '')         // strip anything else
      .replace(/-+/g, '-')                // collapse consecutive hyphens
      .replace(/^-+|-+$/g, '');           // trim leading/trailing hyphens

  const handleAgencyChange = (e) => {
    const val = e.target.value;
    setForm({ ...form, agency_name: val, agent_slug: toSlug(val) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation (replaces browser-native HTML5 validation,
    // which shows unhelpful messages like "The string did not match the expected pattern" in Safari)
    if (!form.agency_name.trim()) return setError('Vul de naam van uw agentschap in.');
    if (!form.agent_name.trim())  return setError('Vul uw naam in.');
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return setError('Voer een geldig e-mailadres in.');
    if (form.password.length < 8) return setError('Wachtwoord moet minimaal 8 tekens bevatten.');
    if (!form.agent_slug) return setError('Vul een URL-slug in voor uw boekingspagina.');
    if (!/^[a-z0-9-]+$/.test(form.agent_slug))
      return setError('De slug mag alleen kleine letters, cijfers en koppeltekens bevatten.');

    setLoading(true);
    try {
      let res;
      try {
        res = await fetch(`${API_BASE_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
      } catch {
        throw new Error('Kan de server niet bereiken. Controleer of de backend draait op poort 3001.');
      }

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Server is niet bereikbaar. Probeer het later opnieuw.');
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registratie mislukt');
      login(data.supabaseAccessToken, data.supabaseRefreshToken, data.user);
      navigate('/pricing');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: '#f4f6f9' }}>
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--brand)' }}>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-xl font-bold text-gray-900">TravelAIgent</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Account aanmaken</h1>
          <p className="text-gray-500 mt-2">Start vandaag met uw AI-reisassistent</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {error && (
            <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Naam agentschap *</label>
                <input
                  type="text" className="input"
                  placeholder="Reizen aan Zee"
                  autoComplete="organization"
                  value={form.agency_name}
                  onChange={handleAgencyChange}
                />
              </div>
              <div>
                <label className="label">Uw naam *</label>
                <input
                  type="text" className="input"
                  placeholder="Jan de Vries"
                  autoComplete="name"
                  value={form.agent_name}
                  onChange={e => setForm({ ...form, agent_name: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="label">E-mailadres *</label>
              <input
                type="email" className="input"
                placeholder="jan@reizenaanzee.nl"
                autoComplete="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
              />
            </div>

            <div>
              <label className="label">Wachtwoord *</label>
              <input
                type="password" className="input"
                placeholder="Minimaal 8 tekens"
                autoComplete="new-password"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
              />
            </div>

            <div>
              <label className="label">Boekingspagina URL *</label>
              <div className="flex items-center">
                <span className="px-3 py-2 bg-gray-50 border border-r-0 border-gray-200 rounded-l-lg text-sm text-gray-500">
                  travelaigent.app/book/
                </span>
                <input
                  type="text" className="input rounded-l-none"
                  placeholder="jan-reizen"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={form.agent_slug}
                  onChange={e => setForm({ ...form, agent_slug: toSlug(e.target.value) })}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Alleen kleine letters, cijfers en koppeltekens</p>
            </div>

            <button
              type="submit" disabled={loading}
              className="btn-primary w-full justify-center py-3"
            >
              {loading ? <span className="spinner" /> : null}
              {loading ? 'Account aanmaken...' : 'Account aanmaken'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Al een account?{' '}
            <Link to="/login" className="font-medium" style={{ color: 'var(--brand)' }}>
              Log hier in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
